import React, { useEffect } from 'react';
import Table from '../../atoms/Table';
import { Icon } from '@material-ui/core';
import { VisitReservationInfo } from '../../../redux/reducers/VisitReservationInfoReducer';
import { REF_CUSTOMER_AGREE_STATUS_TYPE, BEF_CALL_TYPE, HEARING_TYPE, LINE_CONTACT_TYPE } from '../../../Common';
import { getPcmCdNmByPcmCdDivisionFromMasterInfo } from '../../../redux/reducers/MasterInfoReducer/utils/MasterInfoStateUtils';
import { PCM_CD_DIVISION } from '../../../redux/reducers/MasterInfoReducer/definitions/PcmCdDivision';
import { MasterInfoState } from '../../../redux/reducers/MasterInfoReducer';
import { useDispatch } from 'react-redux';
import { VisitReservationBasicInfo } from '../../../redux/reducers/HearingInfoReducer';
import { updateVisitReservationInfo } from '../../../redux/operations/HearingInfo';
import LineIcon from '../../atoms/LineIcon';
import QRCodeIcon from '../../atoms/QRCodeIcon';
import { GASpotEventState } from '../NegotiationCountList';

/** 強調表示項目 */
const EMPHASIZE_ITEMS = {
  /** 照会 */
  INQUIRY: 'inquiry',
  /** 架電 */
  CALL: 'call',
  /** LINEステータス */
  LINE_CONTACT_FLG: 'lineContactFlg',
  /** ヒアリングクルー */
  HEARING_CREW: 'hearingCrew',
} as const;
type EmphasizeItems = typeof EMPHASIZE_ITEMS[keyof typeof EMPHASIZE_ITEMS];

export interface VistReservationTableProps {
  masterInfo: MasterInfoState;
  itemList: VisitReservationInfo[] | undefined;
  isPastDay: boolean;
  gaSpotEvent: GASpotEventState;
}

const VistReservationTable: React.FC<VistReservationTableProps> = ({ itemList, masterInfo, isPastDay, gaSpotEvent }) => {
  const dispatch = useDispatch();

  const handleOpenHearingResponseInfoModal = (dataRecord: VisitReservationInfo) => () => {
    const visitReservationInfo: VisitReservationBasicInfo = {
      code: dataRecord.code,
      firstInsDate: dataRecord.firstInsDate,
      hearingId: dataRecord.hearingId,
    };
    updateVisitReservationInfo(visitReservationInfo, dispatch);
  };

  /**
   * 強調表示のcssクラスを返す
   * @param item 項目
   * @param value 強調表示項目値
   * @returns cssクラス
   */
  const getClassNamesByItem = (item: EmphasizeItems, value: string) => {
    const EMPHASIZE_CLASS_NAME = 'uc-announce--danger';
    switch (item) {
      case EMPHASIZE_ITEMS.INQUIRY:
        return value ===
          getPcmCdNmByPcmCdDivisionFromMasterInfo(
            masterInfo,
            PCM_CD_DIVISION.REF_CUSTOMER_AGREE_STATUS,
            REF_CUSTOMER_AGREE_STATUS_TYPE.NOT_IMPLEMENTED
          )
          ? EMPHASIZE_CLASS_NAME
          : '';
      case EMPHASIZE_ITEMS.CALL:
        return value === getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.BEF_CALL, BEF_CALL_TYPE.NOT_IMPLEMENTED) ||
          value === getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.BEF_CALL, BEF_CALL_TYPE.RE_CALL)
          ? EMPHASIZE_CLASS_NAME
          : '';
      case EMPHASIZE_ITEMS.HEARING_CREW:
        return value === getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.HEARING, HEARING_TYPE.NOT_IMPLEMENTED)
          ? EMPHASIZE_CLASS_NAME
          : '';
      case EMPHASIZE_ITEMS.LINE_CONTACT_FLG:
        return value ===
          getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.LINE_CONTACT_FLG, LINE_CONTACT_TYPE.CONFIRMATION_REQUIRED)
          ? EMPHASIZE_CLASS_NAME
          : '';
    }
  };

  useEffect(() => {
    const { className, spotEvent } = gaSpotEvent;
    document.querySelectorAll(`.${className}`).forEach((element) => element?.setAttribute('onclick', spotEvent));
  }, []);

  return (
    <>
      <Table classNameImportant='visit-reservation-table'>
        <Table.THead>
          <Table.Tr className='visit-reservation-table__header'>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-sent-at`}
              data-testid='visit-reservation-table--reservation-time-header'>
              予約時刻
            </Table.Th>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-code`}
              data-testid='visit-reservation-table--code-header'>
              案内番号
            </Table.Th>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-business`}
              data-testid='visit-reservation-table--business-header'>
              業務
            </Table.Th>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-staff`}
              data-testid='visit-reservation-table--staff-header'>
              担当者
            </Table.Th>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-route`}
              data-testid='visit-reservation-table--route-header'>
              ルート
            </Table.Th>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-movie`}
              data-testid='visit-reservation-table--movie-header'>
              動画
            </Table.Th>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-inquiry`}
              data-testid='visit-reservation-table--inquiry-header'>
              照会
            </Table.Th>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-call`}
              data-testid='visit-reservation-table--call-header'>
              架電/LINE
            </Table.Th>
            <Table.Th
              className={`u-bg-background-alt visit-reservation-table__th is-hearing`}
              data-testid='visit-reservation-table--hearing-header'>
              ヒアリング
            </Table.Th>
          </Table.Tr>
        </Table.THead>
        <Table.TBody>
          {itemList?.map((item: VisitReservationInfo, index: number) => (
            <React.Fragment key={index}>
              <Table.Tr className='visit-reservation-table__row'>
                {/* 予約時刻 */}
                <Table.Td
                  className='visit-reservation-table__td is-sent-at u-white-space-nowrap'
                  data-testid={`visit-reservation-table--reservation-time`}>
                  {item.sendDatetime}
                </Table.Td>
                {/* 案内番号 */}
                <Table.Td
                  key={`visit-reservation-table__code-${index}`}
                  className='visit-reservation-table__td is-code u-white-space-nowrap'
                  data-testid={`visit-reservation-table--code`}>
                  <div
                    data-testid='visit-reservation-table--codeText'
                    onClick={handleOpenHearingResponseInfoModal(item)}
                    className='visit-reservation-text__code'>
                    {item.code}
                  </div>
                </Table.Td>
                {/* 業務 */}
                <Table.Td className='visit-reservation-table__td is-business' data-testid={`visit-reservation-table--business`}>
                  <span className='u-ellipsis-2'>{item.business}</span>
                </Table.Td>
                {/* 担当者 */}
                <Table.Td className='visit-reservation-table__td is-staff' data-testid={`visit-reservation-table--staff`}>
                  <span className='u-ellipsis-2'>{item.staff}</span>
                </Table.Td>
                {/* ルート */}
                <Table.Td
                  className='visit-reservation-table__td is-route u-white-space-nowrap'
                  data-testid={`visit-reservation-table--route`}>
                  {item.route}
                </Table.Td>
                {/* 動画 */}
                <Table.Td
                  className='visit-reservation-table__td is-movie u-white-space-nowrap'
                  data-testid={`visit-reservation-table--movie`}>
                  {item.movie}
                </Table.Td>
                {/* 照会 */}
                <Table.Td
                  className={`visit-reservation-table__td is-inquiry u-white-space-nowrap ${
                    isPastDay ? '' : getClassNamesByItem(EMPHASIZE_ITEMS.INQUIRY, item.inquiry)
                  }`}
                  data-testid={`visit-reservation-table--inquiry`}>
                  {isPastDay ? '-' : item.inquiry}
                </Table.Td>
                {/* 架電 */}
                <Table.Td className='visit-reservation-table__td is-call' data-testid='visit-reservation-table--call-container'>
                  {/* 架電/LINE */}
                  <p className={`u-white-space-nowrap u-mb4 ${getClassNamesByItem(EMPHASIZE_ITEMS.CALL, item.call)}`}>
                    <Icon className='u-text-middle u-mr4'>call</Icon>
                    <span className='u-text-middle' data-testid='visit-reservation-table--call'>
                      {item.call}
                    </span>
                  </p>
                  {/* LINEステータス */}
                  <p
                    className={`u-white-space-nowrap ${getClassNamesByItem(
                      EMPHASIZE_ITEMS.LINE_CONTACT_FLG,
                      item.lineContactFlg as string
                    )}`}>
                    <LineIcon className='u-text-middle u-mr4' />
                    <span className='u-text-middle' data-testid='visit-reservation-table--line-status'>
                      {item.lineContactFlg}
                    </span>
                  </p>
                </Table.Td>
                {/* ヒアリング */}
                <Table.Td className='visit-reservation-table__td is-hearing' data-testid={`visit-reservation-table--hearing`}>
                  {/* ヒアリングWEB */}
                  <p className='u-white-space-nowrap u-mb4'>
                    <Icon className='u-text-middle u-mr4'>language</Icon>
                    <span className='u-text-middle' data-testid='visit-reservation-table--hearing-web'>
                      {item.hearingWeb}
                    </span>
                  </p>
                  {/* 「ヒアリングQR」ステータス */}
                  {item.hearingQr && (
                    <p className='u-white-space-nowrap u-mb4'>
                      <QRCodeIcon className='u-text-middle u-mr4' />
                      <span className='u-text-middle' data-testid='visit-reservation-table--hearing-qa'>
                        {item.hearingQr}
                      </span>
                    </p>
                  )}
                  {/* ヒアリングクルー */}
                  <p className={`u-white-space-nowrap ${getClassNamesByItem(EMPHASIZE_ITEMS.HEARING_CREW, item.hearing)}`}>
                    <Icon className='u-text-middle u-mr4'>person</Icon>
                    <span className='u-text-middle' data-testid='visit-reservation-table--hearing-crew'>
                      {item.hearing}
                    </span>
                  </p>
                </Table.Td>
              </Table.Tr>
              <Table.Tr className='visit-reservation-table__row-space'></Table.Tr>
            </React.Fragment>
          ))}
        </Table.TBody>
      </Table>
    </>
  );
};

export default VistReservationTable;
